import {inject, Injectable} from '@angular/core';
import {SupportedLocalesEnum} from '../../../enums/supported-locales.enum';
import {LanguageService} from '../../language/language.service';

@Injectable({
    providedIn: 'root'
})
export class DateMappingService {
    private readonly languageService: LanguageService = inject(LanguageService);

    private get browserLocale(): Intl.Locale {
        return this.languageService.getCurrentBrowserIntlLocale();
    }

    /**
     * Map a given string value to a date.
     * @param stringValue - The string value that should be parsed to a date. The string must be in a standardised format, e.g.: YYYY-MM-DDTHH:mm:ssZ.
     * @returns The mapped date. Undefined will be returned if the given string cannot be parsed to a date.
     */
    public mapStringToDate(stringValue: string): Date | undefined {
        let dateValue: Date;

        if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
            const parts = stringValue.split('-')
                .map(Number);
            dateValue = new Date(parts[0], parts[1] - 1, parts[2]);
        } else {
            dateValue = new Date(stringValue);
        }

        if (Number.isNaN(dateValue.valueOf())) {
            return undefined;
        }

        return dateValue;
    }

    /**
     * Map a free-form date string (e.g. '16.9.2026', '9/16/2026', '16. September 2026', '09.2026') to a date.
     * A slash separated date is read as month/day/year, a dot or dash separated one as day.month.year; if that
     * yields a month above twelve, day and month are swapped. A two digit year is completed with the current
     * century and a date without a day resolves to the first of the month.
     * @param stringValue - The string value that should be parsed to a date.
     * @returns The mapped date. Undefined will be returned if the given string is not a date or is implausible.
     */
    public mapFreeFormDateStringToDate(stringValue: string): Date | undefined {
        const trimmedValue = stringValue.trim();

        return this.mapNumericDateString(trimmedValue)
            ?? this.mapMonthNameDateString(trimmedValue)
            ?? this.mapMonthAndYearString(trimmedValue);
    }

    /**
     * Map a given date to a short date string (e.g. '26.08.2025').
     * @param date - The date value that should be parsed.
     * @param useBrowserLocale - Use the browser locale for mapping instead of the user locale
     * @returns The short date string representation of the date in the user's current locale.
     */
    public mapDateToShortLocaleDateString(date: Date, useBrowserLocale: boolean = false): string {
        const currentIntlLocale = this.languageService.getCurrentIntlLocale() ?? this.languageService.getDefaultLocale();
        const locale = useBrowserLocale ? this.browserLocale.baseName : currentIntlLocale.baseName;

        return Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
            .format(date);
    }

    /**
     * Map a given date to a long date string (e.g. 'Tuesday, August 5, 2025').
     * @param date - The date value that should be parsed.
     * @param useBrowserLocale - Use the browser locale for mapping instead of the user locale
     * @returns The long date string representation of the date in the user's current locale.
     */
    public mapDateToLongLocaleDateString(date: Date, useBrowserLocale: boolean = false): string {
        const currentIntlLocale = this.languageService.getCurrentIntlLocale() ?? this.languageService.getDefaultLocale();
        const locale = useBrowserLocale ? this.browserLocale.baseName : currentIntlLocale.baseName;

        return Intl.DateTimeFormat(locale, {
                day: 'numeric',
                weekday: 'long',
                month: 'long',
                year: 'numeric'
            })
            .format(date);
    }

    /**
     * Map a given date to a short date with time string (e.g. '26.08.2025, 12:00').
     * @param date - The date value that should be parsed.
     * @param useBrowserLocale - Use the browser locale for mapping instead of the user locale
     * @returns The short date with time string representation of the date in the user's current locale.
     */
    public mapDateToShortLocaleDateTimeString(date: Date, useBrowserLocale: boolean = false): string {
        const currentIntlLocale = this.languageService.getCurrentIntlLocale() ?? this.languageService.getDefaultLocale();
        const locale = useBrowserLocale ? this.browserLocale.baseName : currentIntlLocale.baseName;

        return Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
            .format(date);
    }

    /**
     * Map a given date to an ISO 8601 string based on local time (not UTC).
     * @param date - The date value to format.
     * @param isDateOnly - When `true`, returns only the date portion (e.g. '2025-08-26').
     *                     When `false` (default), returns date and time (e.g. '2025-08-26T12:00:00').
     * @returns An ISO 8601 formatted string in local time.
     */
    public mapDateToLocaleISOString(date: Date, isDateOnly: boolean = false): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1)
            .padStart(2, '0');
        const day = String(date.getDate())
            .padStart(2, '0');

        if (isDateOnly) {
            return `${year}-${month}-${day}`;
        }

        const hours = String(date.getHours())
            .padStart(2, '0');
        const minutes = String(date.getMinutes())
            .padStart(2, '0');
        const seconds = String(date.getSeconds())
            .padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }

    private mapNumericDateString(stringValue: string): Date | undefined {
        const match = /^(\d{1,2})([./-])(\d{1,2})\2(\d{2}|\d{4})$/.exec(stringValue);

        if (!match) {
            return undefined;
        }

        const isMonthFirst = match[2] === '/';
        let day = Number(isMonthFirst ? match[3] : match[1]);
        let month = Number(isMonthFirst ? match[1] : match[3]);

        if (month > 12 && day <= 12) {
            [day, month] = [month, day];
        }

        return this.createValidatedDate(this.mapYearStringToYear(match[4]), month, day);
    }

    private mapMonthNameDateString(stringValue: string): Date | undefined {
        const dayFirstMatch = /^(\d{1,2})\.?\s+(\p{L}+\.?)\s+(\d{2}|\d{4})$/u.exec(stringValue);

        if (dayFirstMatch) {
            return this.createDateByMonthName(dayFirstMatch[2], dayFirstMatch[3], Number(dayFirstMatch[1]));
        }

        const monthFirstMatch = /^(\p{L}+\.?)\s+(\d{1,2}),?\s+(\d{2}|\d{4})$/u.exec(stringValue);

        if (monthFirstMatch) {
            return this.createDateByMonthName(monthFirstMatch[1], monthFirstMatch[3], Number(monthFirstMatch[2]));
        }

        return undefined;
    }

    private mapMonthAndYearString(stringValue: string): Date | undefined {
        const numericMatch = /^(\d{1,2})[./-](\d{4})$/.exec(stringValue);

        if (numericMatch) {
            return this.createValidatedDate(Number(numericMatch[2]), Number(numericMatch[1]), 1);
        }

        const isoMatch = /^(\d{4})-(\d{1,2})$/.exec(stringValue);

        if (isoMatch) {
            return this.createValidatedDate(Number(isoMatch[1]), Number(isoMatch[2]), 1);
        }

        const monthNameMatch = /^(\p{L}+\.?)\s+(\d{4})$/u.exec(stringValue);

        if (monthNameMatch) {
            return this.createDateByMonthName(monthNameMatch[1], monthNameMatch[2], 1);
        }

        return undefined;
    }

    private createDateByMonthName(monthName: string, yearString: string, day: number): Date | undefined {
        const month = this.mapMonthNameToMonthNumber(monthName);

        if (!month) {
            return undefined;
        }

        return this.createValidatedDate(this.mapYearStringToYear(yearString), month, day);
    }

    private mapMonthNameToMonthNumber(monthName: string): number | undefined {
        const normalizedName = this.normalizeMonthName(monthName);

        if (normalizedName.length < 3) {
            return undefined;
        }

        for (const localeName of [SupportedLocalesEnum.DeDe, SupportedLocalesEnum.EnUs]) {
            const dateTimeFormat = Intl.DateTimeFormat(localeName, {month: 'long'});

            for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
                if (this.normalizeMonthName(dateTimeFormat.format(new Date(2025, monthIndex, 1))).startsWith(normalizedName)) {
                    return monthIndex + 1;
                }
            }
        }

        return undefined;
    }

    private normalizeMonthName(monthName: string): string {
        return monthName.toLowerCase()
            .replace(/\.$/, '');
    }

    private mapYearStringToYear(yearString: string): number {
        const year = Number(yearString);

        if (yearString.length === 4) {
            return year;
        }

        return Math.floor(new Date().getFullYear() / 100) * 100 + year;
    }

    private createValidatedDate(year: number, month: number, day: number): Date | undefined {
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return undefined;
        }

        const date = new Date(year, month - 1, day);

        if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
            return undefined;
        }

        return date;
    }
}
